/**
 * Horizontal Displacement + Veto/Exclusion Engine (anti-pattern exclusions)
 *
 * Builds upon `hipDisplacementHorizontal`. It reuses the base 5-point Swayback and APT scoring
 * (via the exported `scoreSwayback` / `scoreApt` / `sagittalFromLandmarks` helpers) but adds an
 * **exclusion (veto) layer** that runs *before* the winner is chosen.
 *
 * Rationale: certain high-certainty findings make a given compensation pattern biomechanically
 * impossible. When such an "anti-pattern" is detected, the corresponding orientation's score is
 * forced to **zero**, steering the diagnosis toward the correct reality. SoP table-test vetoes take
 * priority over the visual image vetoes because "you cannot cheat a bad measurement" — a highly
 * compensatory client can fake visual tests by arching their back, but cannot fake a physical
 * range-of-motion measurement.
 *
 * Scoring (per side, inherited from the base strategy, max 5):
 *   Swayback: hip markedly anterior +2 / mildly +1, shoulders behind hips (“C”) +2 / mild +1,
 *             posterior pelvic tilt +1, knee flexion +2.
 *   APT:      anterior pelvic tilt +2, shoulders forward of hips +2 / mild +1, knee hyperextension +2,
 *             pelvis anterior & hip not far forward +1.
 *
 * Vetoes (each forces the named orientation's final score to 0):
 *   Tier 1/2 — SoP (only when `input.sopMetrics` is provided):
 *     R1 Ober/HipExtension full bilaterally  → exclude BOTH (→ Stacked)
 *     R2 femoral IR > femoral ER             → exclude Swayback (pure APT confirmed)
 *     R3 femoral ER > femoral IR             → exclude APT (posterior lower-compression confirmed)
 *     R4 SLR > slrSwaybackVetoMaxDeg         → exclude Swayback
 *   Tier 1/3 — Image (always, from landmarks/parsed strings):
 *     R5 knee Hyperext                       → exclude Swayback
 *     R5 knee Flex beyond extreme threshold  → exclude APT
 *     R6 pelvicTilt ≥ pelvicTiltDegMin       → exclude Swayback (clearly anterior pelvis)
 *     R7 torsoOffset > strictLean            → exclude Swayback (shoulders forward)
 *     R7 torsoOffset < -strictLean           → exclude APT (shoulders back / “C” curve)
 *
 * Resolution: finalScore = excluded ? 0 : rawScore. Higher final score wins; both 0 → Stacked;
 * on a tie with neither excluded, a large hip-forward offset grants Swayback the tie-break (base rule).
 *
 * Authoritative thresholds: `evaluation-rules.yaml` → `posture.thresholds` (+ `posture.thresholds.vetoExclusion`).
 */

import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
  PostureStrategySopMetrics,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import {
  MAX_ORIENTATION_POINTS,
  sagittalFromLandmarks,
  scoreApt,
  scoreSwayback,
} from './hip-displacement-horizontal-strategy';
import { logPostureClassificationDebug } from './posture-classification-debug';

const STRATEGY_ID = 'hipDisplacementHorizontalVetoExclusion';
const DEBUG_LOG_PREFIX = '[hipDisplacementHorizontalVeto]';
const STRATEGY_NAME =
  'Horizontal Displacement + Veto/Exclusion Engine (anti-pattern exclusions)';

interface VetoThresholds {
  slrSwaybackVetoMaxDeg: number;
  kneeFlexionExtremeDegMin: number;
  fullHipExtensionDeg: number;
  oberNeutralDeg: number;
}

const DEFAULT_VETO_THRESHOLDS: VetoThresholds = {
  slrSwaybackVetoMaxDeg: 45,
  kneeFlexionExtremeDegMin: 10,
  fullHipExtensionDeg: 0,
  oberNeutralDeg: 0,
};

function readVetoThresholds(rules: any): VetoThresholds {
  const v = rules?.vetoExclusion ?? {};
  return {
    slrSwaybackVetoMaxDeg: v.slrSwaybackVetoMaxDeg ?? DEFAULT_VETO_THRESHOLDS.slrSwaybackVetoMaxDeg,
    kneeFlexionExtremeDegMin:
      v.kneeFlexionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeFlexionExtremeDegMin,
    fullHipExtensionDeg: v.fullHipExtensionDeg ?? DEFAULT_VETO_THRESHOLDS.fullHipExtensionDeg,
    oberNeutralDeg: v.oberNeutralDeg ?? DEFAULT_VETO_THRESHOLDS.oberNeutralDeg,
  };
}

const sumDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) : null;
};

const avgDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

const bothAtLeast = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a >= threshold &&
  b >= threshold;

const bothAtMost = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a <= threshold &&
  b <= threshold;

interface VetoState {
  isSwaybackExcluded: boolean;
  isAptExcluded: boolean;
  forceStacked: boolean;
  evidence: string[];
}

function applySopVetoes(
  sop: PostureStrategySopMetrics | undefined,
  veto: VetoThresholds,
  state: VetoState
): void {
  if (!sop) return;

  // R1 — Ober's / hip-extension full bilaterally → no SB/APT compensation possible → Stacked.
  const hipExtensionFullBilateral = bothAtLeast(
    sop.hipExtensionLeft,
    sop.hipExtensionRight,
    veto.fullHipExtensionDeg
  );
  const obersNeutralBilateral = bothAtMost(sop.oberLeft, sop.oberRight, veto.oberNeutralDeg);
  if (hipExtensionFullBilateral || obersNeutralBilateral) {
    state.isSwaybackExcluded = true;
    state.isAptExcluded = true;
    state.forceStacked = true;
    state.evidence.push(
      `[VETO] SoP: full hip extension / neutral Ober's bilaterally → no SB/APT compensation → Stacked`
    );
  }

  // R2 / R3 — femoral IR vs ER asymmetry (bilateral totals).
  const irTotal = sumDefined(sop.femoralIrLeft, sop.femoralIrRight);
  const erTotal = sumDefined(sop.femoralErLeft, sop.femoralErRight);
  if (irTotal !== null && erTotal !== null) {
    if (irTotal > erTotal) {
      state.isSwaybackExcluded = true;
      state.evidence.push(
        `[VETO] SoP: femoral IR (${irTotal.toFixed(0)}°) > ER (${erTotal.toFixed(0)}°) → excludes Swayback (anterior orientation confirmed)`
      );
    } else if (erTotal > irTotal) {
      state.isAptExcluded = true;
      state.evidence.push(
        `[VETO] SoP: femoral ER (${erTotal.toFixed(0)}°) > IR (${irTotal.toFixed(0)}°) → excludes APT (posterior lower compression confirmed)`
      );
    }
  }

  // R4 — high SLR excludes Swayback.
  const avgSlr = avgDefined(sop.slrLeft, sop.slrRight);
  if (avgSlr !== null && avgSlr > veto.slrSwaybackVetoMaxDeg) {
    state.isSwaybackExcluded = true;
    state.evidence.push(
      `[VETO] SoP: SLR (${avgSlr.toFixed(0)}°) > ${veto.slrSwaybackVetoMaxDeg}° → excludes Swayback`
    );
  }
}

function applyImageVetoes(
  args: {
    kneeFlex: boolean;
    kneeHyperext: boolean;
    kneeFlexDeg: number;
    pelvicTilt: number;
    torsoOffsetCm: number;
    pelvicTiltDegMin: number;
    strictLean: number;
    kneeFlexionExtremeDegMin: number;
  },
  state: VetoState
): void {
  const {
    kneeFlex,
    kneeHyperext,
    kneeFlexDeg,
    pelvicTilt,
    torsoOffsetCm,
    pelvicTiltDegMin,
    strictLean,
    kneeFlexionExtremeDegMin,
  } = args;

  // R5 — knee vetoes.
  if (kneeHyperext) {
    state.isSwaybackExcluded = true;
    state.evidence.push('[VETO] Image: knee hyperextension → excludes Swayback');
  }
  if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > kneeFlexionExtremeDegMin) {
    state.isAptExcluded = true;
    state.evidence.push(
      `[VETO] Image: extreme knee flexion (~${kneeFlexDeg.toFixed(0)}° > ${kneeFlexionExtremeDegMin}°) → excludes APT`
    );
  }

  // R6 — clearly anterior pelvis excludes Swayback.
  if (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) {
    state.isSwaybackExcluded = true;
    state.evidence.push(
      `[VETO] Image: anterior pelvic tilt (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°) → excludes Swayback`
    );
  }

  // R7 — torso offset vetoes.
  if (isFinite(torsoOffsetCm)) {
    if (torsoOffsetCm > strictLean) {
      state.isSwaybackExcluded = true;
      state.evidence.push(
        `[VETO] Image: shoulders forward of hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm > ${strictLean} cm) → excludes Swayback`
      );
    } else if (torsoOffsetCm < -strictLean) {
      state.isAptExcluded = true;
      state.evidence.push(
        `[VETO] Image: shoulders behind hips / “C” curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm < -${strictLean} cm) → excludes APT`
      );
    }
  }
}

export const hipDisplacementHorizontalVetoExclusionStrategy: PostureOrientationClassificationStrategy =
  {
    id: STRATEGY_ID,
    displayName: STRATEGY_NAME,
    maxClassificationPoints: MAX_ORIENTATION_POINTS,

    classify(
      input: PostureClassificationStrategyInput,
      context: PostureClassificationStrategyContext
    ): PostureClassificationStrategyResult | null {
      const rules = (getEvaluationRules() as any).posture.thresholds;
      const veto = readVetoThresholds(rules);
      const kneeThresholds = {
        kneeFlexionDegMin: rules.kneeFlexionDegMin,
        kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
      };

      const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
      if (!parsed) return null;

      const fromLm = sagittalFromLandmarks(
        input,
        context,
        kneeThresholds.kneeFlexionDegMin,
        kneeThresholds.kneeHyperextensionDegMin
      );

      const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
      const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
      const kneeHyperext = fromLm
        ? fromLm.kneeHyperext
        : parsed.isKneeHyperext && !parsed.isKneeNeutral;
      const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
      const pelvicTilt =
        fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
      const kneeFlexDeg =
        fromLm?.kneeFlexEvidenceDeg ??
        (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : NaN);
      const kneeHipAntCm =
        fromLm && isFinite(fromLm.kneeHipAntCm) ? fromLm.kneeHipAntCm : parsed.kneeHipAntCm;

      const pelvicTiltDegMin =
        input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;

      // Raw 5-point scores (both sides) reused from the base strategy.
      const sb = scoreSwayback(
        hipFwdCm,
        torsoOffsetCm,
        kneeFlex,
        pelvicTilt,
        rules,
        fromLm?.kneeFlexEvidenceDeg,
        kneeHipAntCm,
        pelvicTiltDegMin
      );
      const apt = scoreApt(
        hipFwdCm,
        torsoOffsetCm,
        kneeHyperext,
        pelvicTilt,
        rules,
        input.clientSex,
        kneeHipAntCm
      );
      const rawScoreSwayback = sb.pts;
      const rawScoreApt = apt.pts;

      // Exclusion (veto) layer.
      const state: VetoState = {
        isSwaybackExcluded: false,
        isAptExcluded: false,
        forceStacked: false,
        evidence: [],
      };
      applySopVetoes(input.sopMetrics, veto, state); // SoP tier first (highest certainty)
      applyImageVetoes(
        {
          kneeFlex,
          kneeHyperext,
          kneeFlexDeg,
          pelvicTilt,
          torsoOffsetCm,
          pelvicTiltDegMin,
          strictLean: rules.strictLean,
          kneeFlexionExtremeDegMin: veto.kneeFlexionExtremeDegMin,
        },
        state
      );

      const finalScoreSwayback = state.isSwaybackExcluded ? 0 : rawScoreSwayback;
      const finalScoreApt = state.isAptExcluded ? 0 : rawScoreApt;

      let orientation: PostureOrientationLabel = 'Stacked';
      let matched = 0;
      let winnerEvidence: string[] = [];
      let decisionPath = '';

      if (state.forceStacked || (finalScoreSwayback === 0 && finalScoreApt === 0)) {
        orientation = 'Stacked';
        matched = 0;
        winnerEvidence = state.forceStacked
          ? []
          : ['No surviving swayback vs anterior-orientation points after exclusions.'];
        decisionPath = state.forceStacked ? 'veto.forceStacked' : 'noSurvivingPoints.stacked';
      } else if (finalScoreSwayback > finalScoreApt) {
        orientation = 'Swayback';
        matched = finalScoreSwayback;
        winnerEvidence = sb.evidence;
        decisionPath = 'sbSurvives';
      } else if (finalScoreApt > finalScoreSwayback) {
        orientation = 'Anterior Pelvic Tilt';
        matched = finalScoreApt;
        winnerEvidence = apt.evidence;
        decisionPath = 'aptSurvives';
      } else {
        // Tie with neither excluded — defer to base hip-forward tie-break.
        if (hipFwdCm >= rules.hipForwardCmMin && finalScoreSwayback > 0) {
          orientation = 'Swayback';
          matched = finalScoreSwayback;
          winnerEvidence = [...sb.evidence, 'Tie-break: large hip–ankle offset favors swayback shift.'];
          decisionPath = 'tieBreak.hipFwdFavorsSwayback';
        } else {
          orientation = 'Anterior Pelvic Tilt';
          matched = finalScoreApt;
          winnerEvidence = [...apt.evidence, 'Tie-break: hip stack not dominant → anterior orientation.'];
          decisionPath = 'tieBreak.hipStackFavorsApt';
        }
      }

      const evidence = [...state.evidence, ...winnerEvidence];

      const confidencePct =
        orientation === 'Stacked'
          ? Math.min(
              100,
              Math.round(
                (1 -
                  Math.max(finalScoreSwayback, finalScoreApt) /
                    Math.max(1, MAX_ORIENTATION_POINTS * 2)) *
                  100
              )
            )
          : Math.min(100, Math.round((matched / MAX_ORIENTATION_POINTS) * 100));

      logPostureClassificationDebug(DEBUG_LOG_PREFIX, 'veto/exclusion classification', {
        rawScoreSwayback,
        rawScoreApt,
        finalScoreSwayback,
        finalScoreApt,
        isSwaybackExcluded: state.isSwaybackExcluded,
        isAptExcluded: state.isAptExcluded,
        forceStacked: state.forceStacked,
        orientation,
        decisionPath,
        hasSopMetrics: !!input.sopMetrics,
        vetoEvidence: state.evidence,
      });

      return {
        orientation,
        confidencePct,
        matchedPoints: matched,
        maxPoints: MAX_ORIENTATION_POINTS,
        strategyId: STRATEGY_ID,
        strategyDisplayName: STRATEGY_NAME,
        evidence,
      };
    },
  };
